/**
 * Microphone Process
 * 
 * **CRITICAL: Primary user input stream for decision-making application**
 * 
 * This process captures user voice input and emits it as a public event stream
 * via the message bus. Other processes can listen to 'user.voice' events to
 * consume speech input for AI decision-making, command processing, etc.
 * 
 * **HEADLESS PROCESS:** This process runs headless (no UI view).
 * The visual representation is the metaball in the 3D scene (rendered in the background
 * and conditionally in an overlay). The metaball indicates:
 * - If the user is talking (listening state)
 * - If the AI is speaking
 * - If the AI is trying to call the user
 * - General status representation
 * 
 * Integrates cross-browser speech recognition with spacebar hold (push-to-talk)
 * and double-tap spacebar (toggle) support.
 * 
 * **Event Stream:**
 * - Emits 'user.voice' events with transcript, timestamp on the bus
 * - Any process can listen: bus.on('user.voice', (payload) => { ... })
 * - Console logs all speech for debugging
 * 
 * **Characteristics:**
 * - Priority: NORMAL
 * - Lifecycle: PRESERVABLE
 * - Views: Headless (kind: 'none') - metaball is visual representation
 */

import type { ProcessContext } from '../../systems/process.js';
import { ProcessPriority } from '../../systems/process.js';
import { BaseProcess } from '../../systems/base-process.js';
import { BaseViewHTML } from '../../systems/base-view-html.js';
import { registerProcess } from '../../systems/registry.js';
import { bus } from '../../core/events.js';
import { createSpeechRecognizer, type SpeechRecognizer, type LanguageMetadata } from '../../services/speech-recognition/index.js';
import { drawRoundedButton, roundedRect } from '../../rendering/shapes.js';
import { drawDismissButton } from '../../rendering/interactions.js';
import { GridPositionPreference } from '../../grid-allocation-v2/types.js';
import { isWakeWordDetectionActive, getCurrentDetector } from '../../services/wake-word/index.js';

interface MicrophoneState {
    isListening: boolean;
    isSpacebarHeld: boolean;
    isButtonHeld: boolean; // Track if button is held down (push-to-talk)
    isAvailable: boolean;
    errorMessage?: string;
}

export class MicrophoneProcess extends BaseProcess {
    kind: string = 'microphone';

    // NOTE: microphoneView removed - process is now headless, sphere serves as visual representation
    // private microphoneView: MicrophoneView | null = null;
    private state: MicrophoneState = {
        isListening: false,
        isSpacebarHeld: false,
        isButtonHeld: false,
        isAvailable: true,
        errorMessage: undefined
    };
    private spacebarKeydownHandler: ((e: KeyboardEvent) => void) | null = null;
    private spacebarKeyupHandler: ((e: KeyboardEvent) => void) | null = null;
    private speechRecognizer: SpeechRecognizer | null = null;
    
    // Double-tap spacebar for toggle
    private lastSpacebarPressTime: number = 0;
    private readonly DOUBLE_TAP_MAX_DURATION_MS = 300; // Max time between taps for double-tap

    constructor(id: string) {
        super(id);
        // Use default metadata from BaseProcess (priority: NORMAL, category: 'app')
    }

    protected onStart(ctx: ProcessContext): void {
        // Set up listener for stop requests
        this.setupStopRequestListener();
        
        // Create cross-browser speech recognizer with automatic fallback
        // NOTE: autoRestart disabled to prevent flickering - we'll handle restarts manually if needed
        this.speechRecognizer = createSpeechRecognizer({
            continuous: true,
            autoRestart: false, // Disabled to prevent flickering - manual restart only
            lang: 'en-US'
        });

        // Check if speech recognition is available
        // The recognizer will detect Web Speech API availability
        this.checkAvailability();

        // NOTE: Microphone process is headless - the metaball in the 3D scene serves as its visual representation
        // The metaball rendering in the background 3D scene (and conditionally in an overlay) is the "head" of this process
        // It indicates:
        // - If the user is talking (listening state)
        // - If the AI is speaking
        // - If the AI is trying to call the user
        // - General status representation
        // Do not create the default gold concentric ring view - process runs headless
        
        // Register as headless process (no UI view, metaball is the visual representation)
        const headlessView = this.createHeadlessView(ctx);
        ctx.registerView(headlessView);
        
        // Set up spacebar handlers
        this.setupSpacebarHandlers();

        bus.emit('process.started', { processId: this.id, kind: this.kind });
    }

    /**
     * Check if speech recognition is available
     */
    private checkAvailability(): void {
        // Check Web Speech API availability
        const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        const isAvailable = typeof SpeechRecognition !== 'undefined';

        this.state.isAvailable = isAvailable;

        if (!isAvailable) {
            this.state.errorMessage = 'Speech recognition requires Chrome/Edge or a service-based solution.';
        }

        this.updateView();
    }

    protected onStop(): void {
        // Stop listening before destroying process
        this.stopListening();
        this.cleanupSpacebarHandlers();
    }

    /**
     * Set up spacebar handlers for hold-to-activate and double-tap toggle
     */
    private setupSpacebarHandlers(): void {
        this.spacebarKeydownHandler = (e: KeyboardEvent) => {
            // Only handle spacebar when not typing in input/textarea
            if (e.key === ' ' && !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
                const target = e.target as HTMLElement;
                if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
                    return; // Don't handle spacebar when typing
                }

                const now = Date.now();
                const timeSinceLastPress = now - this.lastSpacebarPressTime;
                
                // Check for double-tap (toggle mode)
                if (timeSinceLastPress < this.DOUBLE_TAP_MAX_DURATION_MS && !this.state.isSpacebarHeld) {
                    // Double-tap detected: toggle microphone
                    e.preventDefault();
                    e.stopPropagation();
                    this.lastSpacebarPressTime = 0; // Reset to prevent triple-tap
                    this.toggleListening();
                    return;
                }
                
                // Single press: push-to-talk (hold to activate)
                if (this.state.isAvailable && !this.state.isSpacebarHeld && !this.state.isListening) {
                    e.preventDefault();
                    e.stopPropagation();
                    this.state.isSpacebarHeld = true;
                    this.startListening();
                }
                
                this.lastSpacebarPressTime = now;
            }
        };

        this.spacebarKeyupHandler = (e: KeyboardEvent) => {
            if (e.key === ' ') {
                // Only deactivate if we were holding spacebar (push-to-talk)
                if (this.state.isSpacebarHeld && this.state.isListening) {
                    e.preventDefault();
                    e.stopPropagation();
                    this.state.isSpacebarHeld = false;
                    this.stopListening();
                }
            }
        };

        // Add event listeners with capture to handle before global handler
        window.addEventListener('keydown', this.spacebarKeydownHandler, true);
        window.addEventListener('keyup', this.spacebarKeyupHandler, true);
    }

    /**
     * Clean up spacebar handlers
     */
    private cleanupSpacebarHandlers(): void {
        if (this.spacebarKeydownHandler) {
            window.removeEventListener('keydown', this.spacebarKeydownHandler, true);
            this.spacebarKeydownHandler = null;
        }
        if (this.spacebarKeyupHandler) {
            window.removeEventListener('keyup', this.spacebarKeyupHandler, true);
            this.spacebarKeyupHandler = null;
        }
    }

    /**
     * Start listening with cross-browser speech recognizer
     * 
     * OPTIMISTIC UPDATE: Immediately updates state and view to green before waiting
     * for speech recognition API to start. This makes the UI feel more responsive.
     * If the API fails, we rollback in the onError callback.
     */
    startListening(): void {
        if (!this.speechRecognizer) return;

        if (this.state.isListening) return; // Already listening

        // OPTIMISTIC UPDATE: Immediately update state and view to show green
        // This makes the UI feel responsive before waiting for the API
        this.state.isListening = true;
        const timestamp = Date.now();
        bus.emit('microphone.turned.on', { timestamp });
        bus.emit('microphone.listening.changed', { isListening: true, timestamp });
        this.updateView(); // Immediately show green state

        // CRITICAL: Remove existing callbacks first to prevent duplicate handlers
        // This prevents callbacks from being added multiple times, causing flickering
        this.speechRecognizer.removeCallbacks();

        // Set up callback to capture ALL speech
        // This captures everything the user says, not just predefined commands
        this.speechRecognizer.onResult((transcript: string, metadata?: LanguageMetadata) => {
            const timestamp = Date.now();

            // Emit user.voice event on the bus for other processes to consume
            // This is the primary input stream for the decision-making application
            bus.emit('user.voice', {
                transcript,
                timestamp,
                // Language metadata from Assembly AI ALD
                languageCode: metadata?.languageCode || null,
                languageConfidence: metadata?.languageConfidence || null,
                // Duration can be calculated if we track start/end times
                // Tone detection could be added later with audio analysis
            });
        });

        // Set up callback for when speech recognition starts
        // NOTE: With optimistic updates, the state is already set to listening=true
        // This callback just confirms the API started successfully
        this.speechRecognizer.onStart(() => {
            // Confirm optimistic update: state should already be listening=true
            // If it's not, something went wrong - update it
            if (!this.state.isListening) {
                this.state.isListening = true;
                const timestamp = Date.now();
                bus.emit('microphone.turned.on', { timestamp });
                bus.emit('microphone.listening.changed', { isListening: true, timestamp });
                this.updateView();
            }
        });

        // Set up callback for when speech recognition ends
        // NOTE: With autoRestart disabled, this only fires when we explicitly stop
        // or when the user stops speaking (if the API ends naturally)
        this.speechRecognizer.onEnd(() => {
            // Guard: Only update if we're still listening (prevents recursive updates)
            if (this.state.isListening) {
                this.state.isListening = false;

                // Emit events to bus for centralized state and other processes
                const timestamp = Date.now();
                bus.emit('microphone.turned.off', { timestamp });
                bus.emit('microphone.listening.changed', { isListening: false, timestamp });

                // Only update view if we're not in the middle of a spacebar hold
                // (spacebar release will handle the state update)
                if (!this.state.isSpacebarHeld) {
                    this.updateView();
                }
            }
        });

        // Set up callback for errors
        // ROLLBACK: If optimistic update happened, rollback to inactive state on error
        this.speechRecognizer.onError((error: unknown) => {
            // Enhanced error logging with full context
            const errorInfo: any = {
                error: error,
                timestamp: new Date().toISOString(),
                state: { ...this.state },
                browser: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
                secureContext: typeof window !== 'undefined' ? window.isSecureContext : false,
                location: typeof window !== 'undefined' ? window.location.href : 'unknown'
            };

            // Extract additional error details if available
            if (error instanceof Error) {
                errorInfo.message = error.message;
                errorInfo.stack = error.stack;
                if ((error as any).errorCode) {
                    errorInfo.errorCode = (error as any).errorCode;
                }
                if ((error as any).details) {
                    errorInfo.details = (error as any).details;
                }
            }

            // ROLLBACK optimistic update: If we optimistically set listening=true,
            // rollback to false since the API failed
            if (this.state.isListening) {
                this.state.isListening = false;

                // Emit events to bus for centralized state and other processes
                const timestamp = Date.now();
                bus.emit('microphone.turned.off', { timestamp });
                bus.emit('microphone.listening.changed', { isListening: false, timestamp });

                // Update error message with helpful information
                if (error instanceof Error) {
                    this.state.errorMessage = error.message;

                    // Mark as unavailable if it's a browser compatibility or network issue
                    if (error.message.includes('not available') ||
                        error.message.includes('network') ||
                        error.message.includes('service-not-allowed')) {
                        this.state.isAvailable = false;
                    }
                } else {
                    this.state.errorMessage = `Speech recognition error: ${String(error)}`;
                }

                this.updateView(); // Rollback visual state (gold)
            }
        });

        // Start recognition
        this.speechRecognizer.start();
    }

    /**
     * Stop listening with speech recognizer
     * 
     * OPTIMISTIC UPDATE: Immediately updates state and view to gold before stopping
     * the speech recognition API. This makes the UI feel more responsive.
     */
    stopListening(): void {
        if (!this.speechRecognizer) return;

        if (!this.state.isListening) return; // Already stopped

        const timestamp = Date.now();

        // Remove callbacks before stopping (prevents callbacks from firing during stop)
        this.speechRecognizer.removeCallbacks();

        // OPTIMISTIC UPDATE: Immediately update state and view to show gold
        // This makes the UI feel responsive before waiting for the API to stop
        const wasListening = this.state.isListening;
        this.state.isListening = false;
        this.state.isSpacebarHeld = false;
        this.state.isButtonHeld = false; // Clear button hold state

        // Emit events to bus for centralized state and other processes
        if (wasListening) {
            bus.emit('microphone.turned.off', { timestamp });
            bus.emit('microphone.listening.changed', { isListening: false, timestamp });

            // Immediately update view to show gold state (optimistic update)
            this.updateView(); // Immediately show gold state
        }

        // Stop recognition (this might trigger onEnd callback, but we've already removed callbacks)
        this.speechRecognizer.stop();
        
    }

    /**
     * Listen for stop requests from other processes (e.g., window focus detection)
     */
    private setupStopRequestListener(): void {
        bus.on('microphone.stop.requested', (payload: unknown) => {
            const data = payload as { reason: string; timestamp: number };
            this.stopListening();
        });
        
        // Listen for activation requests (e.g., from wake word detection)
        bus.on('microphone.activate.requested', (payload: unknown) => {
            const data = payload as { reason: string; timestamp: number };
            if (!this.state.isListening) {
                this.startListening();
            }
        });
    }

    /**
     * Toggle listening state
     * 
     * CRITICAL: Added debounce to prevent rapid toggling that causes flickering
     */
    private lastToggleTime: number = 0;
    private readonly TOGGLE_DEBOUNCE_MS = 300; // Prevent toggles within 300ms

    toggleListening(): void {
        const now = Date.now();

        // Debounce: Prevent rapid toggles that cause flickering
        if (now - this.lastToggleTime < this.TOGGLE_DEBOUNCE_MS) {
            return;
        }

        this.lastToggleTime = now;

        if (this.state.isListening) {
            this.stopListening();
        } else {
            this.startListening();
        }
    }

    /**
     * Update view to reflect current state
     */
    private updateView(): void {
        // NOTE: microphoneView removed - process is headless, no view to update
        // State updates can be used by the sphere UI if needed
    }

    /**
     * Get current state
     */
    getState(): MicrophoneState {
        return { ...this.state };
    }
}

/**
 * MicrophoneView - extends BaseViewHTML with concentric ring pattern
 */
class MicrophoneView extends BaseViewHTML {
    private state: MicrophoneState = {
        isListening: false,
        isSpacebarHeld: false,
        isButtonHeld: false,
        isAvailable: true,
        errorMessage: undefined
    };
    
    // Initial attention pulse on page load
    private initialPulseStartTime: number | null = null;
    private initialPulseDuration: number = 3000; // 3 seconds of pulsing
    private hasShownInitialPulse: boolean = false;
    
    // Timing for push-to-talk vs toggle detection
    private mouseDownTime: number | null = null;
    private wasListeningBeforeMouseDown: boolean = false; // Track if microphone was listening before mousedown
    private wasQuickClick: boolean = false; // Track if the last interaction was a quick click
    private readonly TOGGLE_CLICK_MAX_DURATION_MS = 500; // < 500ms = toggle click
    private readonly PUSH_TO_TALK_MIN_DURATION_MS = 500; // >= 500ms = push-to-talk

    constructor(
        processId: string,
        private microphoneProcess: MicrophoneProcess,
        initialState: MicrophoneState
    ) {
        super(processId, `${processId}-main`, {
            // 1x1 grid cell - minimal, compact (fixed size)
            acceptableSizes: [{ cols: 1, rows: 1 }], // Fixed: 1x1 preferred, 1x1 minimum
            showDismissButton: false, // Not dismissible - critical input module
            preferredPosition: GridPositionPreference.TOP_RIGHT, // Fixed position in top right
            priority: 1000, // High priority - always allocated first
            allowedPages: [0] // Must stay on main grid
        });
        this.state = { ...initialState };
    }

    mount(container: HTMLElement): void {
        super.mount(container);
        
        // Start initial attention pulse on page load
        this.startInitialAttentionPulse();
        
        // Setup event listeners
        if (this.element) {
            this.setupEventListeners();
        }
    }
    
    unmount(): void {
        // Stop animations
        this.stopGlowAnimation();
        
        // Clean up event listeners
        if (this.element) {
            this.cleanupEventListeners();
        }
        
        super.unmount();
    }
    
    private setupEventListeners(): void {
        if (!this.element) return;
        
        this.element.addEventListener('mousedown', this.handleMouseDown);
        this.element.addEventListener('mouseup', this.handleMouseUp);
        this.element.addEventListener('click', this.handleClick);
    }
    
    private cleanupEventListeners(): void {
        if (!this.element) return;
        
        this.element.removeEventListener('mousedown', this.handleMouseDown);
        this.element.removeEventListener('mouseup', this.handleMouseUp);
        this.element.removeEventListener('click', this.handleClick);
    }
    
    private handleMouseDown = (e: MouseEvent): void => {
        if (!this.state.isAvailable) return;
        
        e.preventDefault();
        e.stopPropagation();
        
        // Record the time and state when mouse is pressed down
        this.mouseDownTime = Date.now();
        this.wasListeningBeforeMouseDown = this.state.isListening;
        this.wasQuickClick = false; // Reset flag
        
        // For push-to-talk: start listening when mouse is pressed down (if not already listening)
        if (!this.state.isListening && !this.state.isButtonHeld) {
            this.state.isButtonHeld = true;
            this.microphoneProcess.startListening();
        } else if (this.state.isListening && !this.state.isButtonHeld) {
            // If already listening, mark button as held for push-to-talk
            this.state.isButtonHeld = true;
        }
        
        this.markDirty();
    };
    
    private handleMouseUp = (e: MouseEvent): void => {
        if (!this.mouseDownTime) return; // No mousedown recorded, ignore
        
        e.preventDefault();
        e.stopPropagation();
        
        const duration = Date.now() - this.mouseDownTime;
        this.wasQuickClick = duration < this.TOGGLE_CLICK_MAX_DURATION_MS;
        
        if (this.wasQuickClick) {
            // Quick click (< 500ms) = toggle click
            // Don't stop here - let onClick handle the toggle
            this.state.isButtonHeld = false;
        } else {
            // Long press (>= 500ms) = push-to-talk: stop listening
            if (this.state.isButtonHeld && this.state.isListening) {
                this.state.isButtonHeld = false;
                this.microphoneProcess.stopListening();
            }
            // Mark that this was NOT a quick click, so onClick won't toggle
            this.mouseDownTime = null;
            this.wasQuickClick = false;
        }
        
        this.markDirty();
    };
    
    private handleClick = (e: MouseEvent): void => {
        if (!this.state.isAvailable) return;
        
        e.preventDefault();
        e.stopPropagation();
        
        // Only handle toggle clicks (quick clicks)
        if (!this.wasQuickClick) {
            return;
        }
        
        // Quick click = toggle
        if (this.wasListeningBeforeMouseDown) {
            // Was already on - toggle it off
            this.microphoneProcess.toggleListening();
        }
        this.state.isButtonHeld = false;
        
        // Reset for next interaction
        this.mouseDownTime = null;
        this.wasQuickClick = false;
        
        this.markDirty();
    };

    protected getContentHTML(): string {
        const isActive = this.state.isListening;
        const isAvailable = this.state.isAvailable;
        const hasError = !!this.state.errorMessage || !isAvailable;
        const showWakeWordHint = !isActive && isAvailable && isWakeWordDetectionActive() && getCurrentDetector() === 'annyang';
        const initialPulseIntensity = this.getInitialPulseIntensity();
        const hasInitialPulse = initialPulseIntensity > 0;
        
        // Generate concentric rings
        const ringCount = 30;
        const rings = Array.from({ length: ringCount }, (_, i) => {
            const radius = 20 + (i * 8);
            const distanceFromCenter = i / ringCount;
            const intensity = 1 - distanceFromCenter * 0.5;
            
            let ringColor = 'rgba(0, 0, 0, 0.15)'; // Inactive
            let ringWidth = '0.8px';
            
            if (isActive || hasInitialPulse) {
                const pulseBlend = initialPulseIntensity;
                const baseOpacity = isActive ? 0.25 : 0.15;
                const pulseOpacity = baseOpacity + (intensity * 0.25) + (pulseBlend * 0.15);
                ringColor = `rgba(102, 204, 255, ${pulseOpacity})`;
                ringWidth = `${0.8 + intensity * 0.7}px`;
            }
            
            return `<div class="mic-ring" style="
                position: absolute;
                left: 50%;
                top: 50%;
                width: ${radius * 2}px;
                height: ${radius * 2}px;
                margin-left: -${radius}px;
                margin-top: -${radius}px;
                border: ${ringWidth} solid ${ringColor};
                border-radius: 50%;
                pointer-events: none;
            "></div>`;
        }).join('');
        
        // Center glow effect
        const glowOpacity = isActive || hasInitialPulse 
            ? (isActive ? 0.3 + (Math.sin(Date.now() * 0.002) + 1) / 2 * 0.2 : 0.3 + initialPulseIntensity * 0.2)
            : 0;
        
        return `
            <style>
                @keyframes mic-pulse {
                    0%, 100% { opacity: 0.3; transform: scale(1); }
                    50% { opacity: 0.5; transform: scale(1.1); }
                }
                @keyframes wake-word-pulse {
                    0%, 100% { opacity: 0.3; }
                    50% { opacity: 0.7; }
                }
                .mic-container {
                    position: relative;
                    width: 100%;
                    height: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    user-select: none;
                }
                .mic-rings-container {
                    position: absolute;
                    width: 100%;
                    height: 100%;
                    pointer-events: none;
                }
                .mic-glow {
                    position: absolute;
                    left: 50%;
                    top: 50%;
                    width: 60px;
                    height: 60px;
                    margin-left: -30px;
                    margin-top: -30px;
                    border-radius: 50%;
                    background: radial-gradient(circle, rgba(102, 204, 255, ${glowOpacity}) 0%, rgba(102, 204, 255, ${glowOpacity * 0.5}) 50%, transparent 100%);
                    pointer-events: none;
                    ${isActive ? 'animation: mic-pulse 1s ease-in-out infinite;' : ''}
                }
                .mic-icon {
                    position: relative;
                    z-index: 2;
                    width: 24px;
                    height: 24px;
                    opacity: ${isActive ? '0' : '0.25'};
                    transition: opacity 0.2s;
                }
                .mic-icon svg {
                    width: 100%;
                    height: 100%;
                }
                .mic-error {
                    position: absolute;
                    left: 50%;
                    top: 50%;
                    width: 8px;
                    height: 8px;
                    margin-left: -4px;
                    margin-top: -4px;
                    background: rgba(200, 50, 50, 0.6);
                    border-radius: 50%;
                    z-index: 3;
                    pointer-events: none;
                }
                .mic-wake-word-hint {
                    position: absolute;
                    left: 50%;
                    top: 20px;
                    transform: translateX(-50%);
                    font-size: 11px;
                    color: rgba(0, 0, 0, 0.5);
                    z-index: 3;
                    pointer-events: none;
                    animation: wake-word-pulse 3s ease-in-out infinite;
                }
            </style>
            <div class="mic-container">
                <div class="mic-rings-container">
                    ${rings}
                </div>
                ${glowOpacity > 0 ? '<div class="mic-glow"></div>' : ''}
                ${!isActive && isAvailable ? `
                    <div class="mic-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                            <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
                            <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                            <line x1="12" y1="19" x2="12" y2="22"/>
                            <line x1="8" y1="22" x2="16" y2="22"/>
                        </svg>
                    </div>
                ` : ''}
                ${hasError ? '<div class="mic-error"></div>' : ''}
                ${showWakeWordHint ? '<div class="mic-wake-word-hint">"Hey, Pivot."</div>' : ''}
            </div>
        `;
    }
    
    protected render(): void {
        super.render();
        
        // Start/stop glow animation based on listening state
        if (this.element && this.state.isListening) {
            this.startGlowAnimation();
        } else {
            this.stopGlowAnimation();
        }
    }
    
    private glowAnimationId: number | null = null;
    
    private startGlowAnimation(): void {
        if (this.glowAnimationId !== null) return;
        
        const animate = () => {
            if (!this.element || !this.state.isListening) {
                this.stopGlowAnimation();
                return;
            }
            
            const glow = this.element.querySelector('.mic-glow') as HTMLElement;
            if (glow) {
                // Update glow opacity based on pulse
                const pulse = (Math.sin(Date.now() * 0.002) + 1) / 2;
                const opacity = 0.3 + pulse * 0.2;
                glow.style.background = `radial-gradient(circle, rgba(102, 204, 255, ${opacity}) 0%, rgba(102, 204, 255, ${opacity * 0.5}) 50%, transparent 100%)`;
            }
            
            this.glowAnimationId = requestAnimationFrame(animate);
        };
        
        this.glowAnimationId = requestAnimationFrame(animate);
    }
    
    private stopGlowAnimation(): void {
        if (this.glowAnimationId !== null) {
            cancelAnimationFrame(this.glowAnimationId);
            this.glowAnimationId = null;
        }
    }
    



    /**
     * Start initial attention pulse on page load
     * Draws user attention when module first appears
     */
    private startInitialAttentionPulse(): void {
        if (this.hasShownInitialPulse) return;

        this.initialPulseStartTime = Date.now();

        const animate = () => {
            if (!this.initialPulseStartTime) {
                return;
            }

            const elapsed = Date.now() - this.initialPulseStartTime;

            if (elapsed < this.initialPulseDuration) {
                // Continue pulsing
                this.markDirty();
                requestAnimationFrame(animate);
            } else {
                // Pulse complete - settle to normal state
                this.initialPulseStartTime = null;
                this.hasShownInitialPulse = true;
                this.markDirty(); // Final render to show normal state
            }
        };

        requestAnimationFrame(animate);
    }

    /**
     * Get initial pulse intensity (0.0 to 1.0)
     * Returns 0 if pulse is complete or not started
     */
    private getInitialPulseIntensity(): number {
        if (!this.initialPulseStartTime || this.hasShownInitialPulse) return 0;

        const elapsed = Date.now() - this.initialPulseStartTime;
        if (elapsed >= this.initialPulseDuration) {
            this.hasShownInitialPulse = true;
            this.initialPulseStartTime = null;
            return 0;
        }

        // Create 3 gentle pulses over the duration
        const progress = elapsed / this.initialPulseDuration; // 0.0 to 1.0
        const pulseCount = 3; // 3 pulses
        const pulsePhase = (progress * pulseCount * Math.PI * 2) % (Math.PI * 2);

        // Gentle sine wave pulse - starts strong, fades out
        const pulse = (Math.sin(pulsePhase) + 1) / 2; // 0.0 to 1.0
        const fadeOut = 1 - progress; // Fade out over time

        return pulse * fadeOut * 0.6; // Max 60% intensity, fades out
    }

    /**
     * Update state and trigger HTML re-render
     */
    updateState(newState: MicrophoneState): void {
        const wasListening = this.state.isListening;

        this.state = { ...newState };

        // Trigger HTML re-render when listening state changes
        this.markDirty();
    }
}

// Register process factory
// Note: Type cast needed due to BaseProcess metadata.category type mismatch with ProcessMetadata
registerProcess('microphone', (id: string) => new MicrophoneProcess(id) as any);

